/**
 * API Service
 * Axios instance with interceptors for authentication
 */

import axios from 'axios';
import API_URL from '../config/api';

// Create axios instance with proper base URL for production
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: false // Set to true if using cookies
});

// Request interceptor - add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Never redirect if already on the login page — prevents infinite reload loop
    const onLoginPage = window.location.pathname === '/login';

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh token (only if we have one)
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const refreshResponse = await axios.post(
            `${API_URL}/api/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          const newToken = refreshResponse.data.token;
          localStorage.setItem('token', newToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('token');
        if (!onLoginPage) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // If refresh also failed or other error
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (!onLoginPage) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
